import {
  APIFieldAttributes,
  DirectedTripleWithFilters,
  MetaModelTriple,
  PARENT_REF_TYPE_NAME,
} from '@ardoq/api-types';
import { uniq } from 'lodash';
import { isTruthy } from '@ardoq/common-helpers';

type TraversalPathsAndEntitiesNameIndices = {
  paths: DirectedTripleWithFilters[][];
  componentNameAndWorkspacesIndex: Map<string, string[]>;
  componentNameAndFieldsIndex: Map<string, string[]>;
  referenceNameAndFieldsIndex: Map<string, string[]>;
  fieldMap: Map<string, APIFieldAttributes>;
  triples?: MetaModelTriple[];
};

const isParentChildReference = (referenceType: string) =>
  referenceType === 'ardoq_parent';

export const findAllParentTypes = (
  initialTypes: string[],
  typeHierarchyMap: ReadonlyMap<string, ReadonlySet<string>>
) => {
  const discoveredTypes = new Set<string>(initialTypes);
  const typesToProcess = [...initialTypes];

  while (typesToProcess.length > 0) {
    const currentType = typesToProcess.pop()!;
    const parentTypes = typeHierarchyMap.get(currentType);

    if (parentTypes) {
      for (const parentType of parentTypes) {
        if (!discoveredTypes.has(parentType)) {
          discoveredTypes.add(parentType);
          typesToProcess.push(parentType);
        }
      }
    }
  }

  return Array.from(discoveredTypes);
};

export const getWorkspaceIdsAndTypeNamesFromTraversalPaths = ({
  paths,
  componentNameAndWorkspacesIndex,
  componentNameAndFieldsIndex,
  referenceNameAndFieldsIndex,
  fieldMap,
  triples,
}: TraversalPathsAndEntitiesNameIndices) => {
  const childToParentTypesMap = new Map<string, Set<string>>();

  const referenceTypeNamesSet = new Set<string>();
  const componentTypeNamesSet = new Set<string>();

  paths.forEach(path => {
    path.forEach(({ sourceType, targetType, referenceType }) => {
      componentTypeNamesSet.add(sourceType);
      componentTypeNamesSet.add(targetType);

      if (!isParentChildReference(referenceType)) {
        referenceTypeNamesSet.add(referenceType);
      }
    });
  });

  const referenceTypeNames = Array.from(referenceTypeNamesSet);
  const componentTypeNames = Array.from(componentTypeNamesSet);

  if (triples && triples.length > 0) {
    for (const triple of triples) {
      if (triple.referenceType === PARENT_REF_TYPE_NAME) {
        const childType = triple.targetComponentType;
        const parentType = triple.sourceComponentType;

        if (!childToParentTypesMap.has(childType)) {
          childToParentTypesMap.set(childType, new Set<string>());
        }

        childToParentTypesMap.get(childType)!.add(parentType);
      }
    }
  }

  const allComponentTypeNames = findAllParentTypes(
    componentTypeNames,
    childToParentTypesMap
  );

  const workspaceIds = uniq(
    allComponentTypeNames.flatMap(
      componentTypeName =>
        componentNameAndWorkspacesIndex.get(componentTypeName) ?? []
    )
  );
  const availableComponentFields = allComponentTypeNames
    .flatMap(typeName => componentNameAndFieldsIndex.get(typeName) ?? [])
    .map(name => fieldMap.get(name) ?? null)
    .filter(isTruthy);
  const availableReferenceFields = referenceTypeNames
    .flatMap(typeName => referenceNameAndFieldsIndex.get(typeName) ?? [])
    .map(name => fieldMap.get(name) ?? null)
    .filter(isTruthy);
  const componentFieldsByType = new Map(
    allComponentTypeNames.map(typeName => [
      typeName,
      (componentNameAndFieldsIndex.get(typeName) || [])
        .map(typeName => fieldMap.get(typeName))
        .filter(isTruthy),
    ])
  );
  const referenceFieldsByType = new Map(
    referenceTypeNames.map(typeName => [
      typeName,
      (referenceNameAndFieldsIndex.get(typeName) || [])
        .map(name => fieldMap.get(name))
        .filter(isTruthy),
    ])
  );
  return {
    workspaceIds,
    componentTypeNames: allComponentTypeNames,
    referenceTypeNames: referenceTypeNames,
    availableComponentFields,
    availableReferenceFields,
    componentFieldsByType,
    referenceFieldsByType,
    componentNameAndFieldsIndex,
    referenceNameAndFieldsIndex,
  };
};
